import { Howl, Howler } from 'howler';

/**
 * AudioBus — thin wrapper over Howler providing the same API surface that the
 * game's AudioManager used to expose with Phaser. Loop management, mute
 * persistence, and silent fall-through when an asset is missing.
 */

const MUTE_KEY = 'spliffo:muted:v1';

export interface AudioAssetDef {
  key: string;
  src: string[];         // one or more formats (mp3/ogg/webm)
  loop?: boolean;
  volume?: number;
}

export class AudioBus {
  private cache = new Map<string, Howl>();
  private activeLoops = new Map<string, Howl>();
  private music: Howl | null = null;
  private musicKey: string | null = null;
  private muted: boolean;

  constructor() {
    this.muted = localStorage.getItem(MUTE_KEY) === '1';
    Howler.mute(this.muted);
  }

  /** Register an asset without forcing a load (Howler lazy-loads on first play). */
  register(def: AudioAssetDef): Promise<void> {
    return new Promise((resolve) => {
      const howl = new Howl({
        src: def.src,
        loop: !!def.loop,
        volume: def.volume ?? 1,
        onload: () => resolve(),
        onloaderror: () => resolve(), // missing files are silent — game still runs
      });
      this.cache.set(def.key, howl);
    });
  }

  has(key: string): boolean {
    return this.cache.has(key);
  }

  play(key: string, opts?: { volume?: number }): void {
    const h = this.cache.get(key);
    if (!h) return;
    const id = h.play();
    if (opts?.volume !== undefined) h.volume(opts.volume, id);
  }

  playMusic(key: string): void {
    if (this.musicKey === key && this.music?.playing()) return;
    this.stopMusic();
    const h = this.cache.get(key);
    if (!h) return;
    this.music = h;
    this.musicKey = key;
    h.volume(0.4);
    h.loop(true);
    h.play();
  }

  stopMusic(): void {
    if (this.music) {
      this.music.stop();
      this.music = null;
      this.musicKey = null;
    }
  }

  /** Start a looping SFX (e.g. ember crackle during inhale). Idempotent per key. */
  playLoop(key: string, volume = 0.5): void {
    if (this.activeLoops.has(key)) return;
    const h = this.cache.get(key);
    if (!h) return;
    h.loop(true);
    h.volume(volume);
    h.play();
    this.activeLoops.set(key, h);
  }

  stopLoop(key: string): void {
    const h = this.activeLoops.get(key);
    if (!h) return;
    h.stop();
    this.activeLoops.delete(key);
  }

  stopAllLoops(): void {
    this.activeLoops.forEach((h) => h.stop());
    this.activeLoops.clear();
  }

  toggleMute(): boolean {
    this.muted = !this.muted;
    Howler.mute(this.muted);
    localStorage.setItem(MUTE_KEY, this.muted ? '1' : '0');
    return this.muted;
  }

  isMuted(): boolean {
    return this.muted;
  }
}

/** Global click sound helper so UI components can fire it without holding a bus ref. */
export const playUIClick = (bus: AudioBus | null): void => {
  if (!bus) return;
  bus.play('sfx_click', { volume: 0.55 });
};
